import { type JSX, Show } from 'solid-js';
import type { GetConfigurationResponse } from '@data-access/connect/tc-issue-management';
import {
  getStatusColor,
  getStatusTextColor,
  getStatusTranslation,
} from '../../utils/issue-icons';

export interface IssueStatusBadgeProps {
  status?: string;
  configuration?: GetConfigurationResponse | null;
}

/**
 * Renders a status chip matching the legacy TopicsList Chips component
 * with `color` and `size="xs"`. Uses the `chips non-editable xs` CSS
 * classes with an inline background-color and contrasting text color.
 */
/* eslint-disable @typescript-eslint/naming-convention -- SolidJS CSSProperties requires kebab-case */
const chipStyle: JSX.CSSProperties = {
  'min-height': '1.25rem',
  height: '1.25rem',
  'padding-left': '0.4rem',
  'padding-right': '0.4rem',
  'font-size': '0.6875rem',
  'font-weight': '600',
  'border-radius': '1rem',
  'white-space': 'nowrap',
  display: 'inline-flex',
  'align-items': 'center',
  overflow: 'hidden',
  margin: '0',
};

const chipLabelStyle: JSX.CSSProperties = {
  'max-width': '100%',
  overflow: 'hidden',
  'text-overflow': 'ellipsis',
};
/* eslint-enable @typescript-eslint/naming-convention */

export function IssueStatusBadge(props: IssueStatusBadgeProps): JSX.Element {
  const bgColor = (): string =>
    getStatusColor(props.configuration ?? null, props.status);

  const textColor = (): string =>
    getStatusTextColor(props.configuration ?? null, props.status);

  const label = (): string => getStatusTranslation(props.status);

  return (
    <Show when={props.status}>
      <span
        style={{
          ...chipStyle,
          'background-color': bgColor(), // eslint-disable-line @typescript-eslint/naming-convention
          color: textColor(),
        }}
      >
        <span style={chipLabelStyle}>{label()}</span>
      </span>
    </Show>
  );
}
